import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  AddWorkReminderModel,
  UpdateWorkReminderModel,
  WorkReminderModel,
} from "../factories/work/model/work-reminder.model";

type GetWorkRemindersParams = {
  orgId: string;
  workId: string;
};

type GetWorkReminderParams = {
  workId: string;
  reminderId: string;
};

export const workRemindersSlice = createApi({
  reducerPath: "workRemindersSlice",
  tagTypes: ["WorkReminder"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getWorkReminders: build.query<WorkReminderModel[], GetWorkRemindersParams>({
      query: ({ orgId, workId }) => ({
        url: "api/job-reminder/get-all",
        method: "GET",
        params: { orgId, workId },
      }),
      transformResponse: (res: WorkReminderModel[]) => {
        return [...(res || [])].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
      },
      providesTags: ["WorkReminder"],
    }),
    getWorkReminder: build.query<WorkReminderModel, GetWorkReminderParams>({
      query: ({ workId, reminderId }) => ({
        url: "api/job-reminder/id",
        method: "GET",
        params: { workId, reminderId },
      }),
      providesTags: ["WorkReminder"],
    }),
    addWorkReminder: build.mutation<WorkReminderModel, AddWorkReminderModel>({
      query: (body) => ({
        url: "api/job-reminder/add",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WorkReminder"],
    }),
    updateWorkReminder: build.mutation<
      WorkReminderModel,
      UpdateWorkReminderModel
    >({
      query: (body) => ({
        url: "api/job-reminder/update",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["WorkReminder"],
    }),
    deleteWorkReminder: build.mutation<unknown, string>({
      query: (reminderId) => ({
        url: "api/job-reminder",
        method: "DELETE",
        params: { reminderId },
      }),
      invalidatesTags: ["WorkReminder"],
    }),
  }),
});

export const {
  useGetWorkRemindersQuery,
  useGetWorkReminderQuery,
  useAddWorkReminderMutation,
  useUpdateWorkReminderMutation,
  useDeleteWorkReminderMutation,
} = workRemindersSlice;
